/// <reference types="google.maps" />
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { format } from "date-fns";

import { BusinessRole } from "fe-shared/src/types/domain";
import { getColor } from "fe-shared/src/helpers/colors";
import { Dialog } from "fe-shared/src/components";
import ParticipantInfo from "../ParticipantInfo";
import { MarkerLocation } from "../Icons";

import {
  getFirstAddressLine,
  getSecondAddressLine,
} from "../../helpers/address";
import { isTransactionWithWeightAndScale } from "../../helpers/transactions";
import {
  Transaction,
  TransactionEvent,
  TransactionEventType,
  TransactionType,
  WarehouseTypeCode,
} from "../../types";

import styles from "./LocationsMap.module.scss";

interface Props {
  transaction: Transaction;
  isStaffView?: boolean;
}

const minimapOptions: google.maps.MapOptions = {
  gestureHandling: "none",
  mapTypeControl: false,
  fullscreenControl: false,
  zoomControl: false,
  scaleControl: false,
  streetViewControl: false,
  maxZoom: 15,
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const dialogMapOptions: google.maps.MapOptions = {
  fullscreenControl: false,
  streetViewControl: false,
};

const MARKER_COLORS_MAP: { [key in TransactionEventType]?: string } = {
  [TransactionEventType.Initiated]: "#9007B6",
  [TransactionEventType.Completed]: `${getColor('--primary-color')}`,
  [TransactionEventType.QrCodeScanned]: "#CD9600",
  [TransactionEventType.PhotoAdded]: "#0B54A4",
};

const LOCATION_MARKER_COLOR = "#63696F";

const getMarkerIcon = (fillColor = "", scale: number) => ({
  scale,
  fillColor,
  anchor: new google.maps.Point(3 * scale, 4 * scale),
  path:
    "M3.27246 6.26033C3.27246 4.02915 5.07701 2.22461 7.30818 2.22461C9.53936 2.22461 11.3439 4.02915 11.3439 6.26033C11.3439 8.66447 8.79564 11.9795 7.75211 13.2421C7.5215 13.5189 7.10063 13.5189 6.87002 13.2421C5.82073 11.9795 3.27246 8.66447 3.27246 6.26033ZM5.86685 6.26033C5.86685 7.05595 6.51257 7.70166 7.30818 7.70166C8.1038 7.70166 8.74951 7.05595 8.74951 6.26033C8.74951 5.46472 8.1038 4.819 7.30818 4.819C6.51257 4.819 5.86685 5.46472 5.86685 6.26033Z",
  fillOpacity: 1,
  strokeWeight: 0,
});

const locationMapper: Partial<Record<BusinessRole, string>> = {
  [BusinessRole.Processor]: "processingLocation",
  [BusinessRole.Collector]: "collectionSite",
  [BusinessRole.OneTimePickup]: "collectionSite",
  [BusinessRole.Hauler]: "storageLocation",
};

export const LocationsMap = ({ transaction, isStaffView }: Props) => {
  const {
    events,
    targetWarehouse,
    sourceWarehouse,
    transactionType,
    isPaperForm,
  } = transaction;
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDRBC6X2T_CtBgiUvErub3jgw-k1DeUq5I",
  });
  const { t } = useTranslation();
  const [isDialogActive, setIsDialogActive] = useState(false);
  const isDeliveryOrReceipt =
    transactionType &&
    [TransactionType.Delivery, TransactionType.Receipt].includes(
      transactionType
    );
  const warehouse = isDeliveryOrReceipt ? targetWarehouse : sourceWarehouse;
  const address =
    warehouse?.location?.address || warehouse?.participant.businessAddress;
  const warehouseBusinessRole =
    warehouse?.participant?.businessRoleCode || BusinessRole.Hauler;

  const isWithWeightAndScale = useMemo(
    () => isTransactionWithWeightAndScale(transaction),
    [transaction]
  );

  const getEventTypeTitleCode = useCallback(
    (eventType: TransactionEventType) => {
      const eventTypeTitle = `transactions.details.eventFullTitles.${eventType}`;

      if (eventType === TransactionEventType.PhotoAdded) {
        return isWithWeightAndScale
          ? `${eventTypeTitle}.scaleTicket`
          : `${eventTypeTitle}.photo`;
      }
      if (eventType === TransactionEventType.Completed && isPaperForm) {
        return "transactions.details.eventFullTitles.paperForm";
      }

      return eventTypeTitle;
    },
    [isWithWeightAndScale, isPaperForm]
  );

  const markers = useMemo(() => {
    const { latitude: lat = 0, longitude: lng = 0 } = address || {};

    const locationTitle = locationMapper[warehouseBusinessRole];

    return [
      {
        lat,
        lng,
        fillColor: LOCATION_MARKER_COLOR,
        title: t(`transactions.details.${transactionType}.${locationTitle}`),
        subtitle: (
          <>
            {getFirstAddressLine(address)}
            <br />
            {getSecondAddressLine(address)}
          </>
        ),
        isVisible:
          !!lat &&
          !!lng &&
          warehouse?.warehouseTypeCode !== WarehouseTypeCode.HaulerVehicle,
      },
      ...events.map((event: TransactionEvent) => ({
        lat: event.latitude,
        lng: event.longitude,
        fillColor: MARKER_COLORS_MAP[event.eventType],
        title: t(getEventTypeTitleCode(event.eventType)),
        subtitle: format(new Date(event.createdDate), "MMM d, yyyy h:mm a"),
        isVisible: !!event.latitude && !!event.longitude,
      })),
    ].filter((m) => m.isVisible);
  }, [
    t,
    events,
    address,
    transactionType,
    warehouse,
    warehouseBusinessRole,
    getEventTypeTitleCode,
  ]);

  const onMapLoad = (map: google.maps.Map, padding = 5) => {
    const bounds = new google.maps.LatLngBounds();

    markers.forEach((marker) => {
      bounds.extend(marker);
    });
    map.fitBounds(bounds, padding);
  };

  return (
    <>
      <div className={styles.miniMap}>
        {isLoaded && (
          <GoogleMap
            onLoad={onMapLoad}
            options={minimapOptions}
            onClick={() => setIsDialogActive(true)}
            mapContainerClassName={styles.mapContainer}
          >
            {markers.map((m) => (
              <Marker
                key={m.lat + m.lng}
                position={m}
                icon={getMarkerIcon(m.fillColor, 1)}
                onClick={() => setIsDialogActive(true)}
              />
            ))}
          </GoogleMap>
        )}
      </div>
      {isLoaded && (
        <Dialog
          width={1000}
          title={null}
          isHeaderHidden
          isActive={isDialogActive}
          onClose={() => setIsDialogActive(false)}
        >
          <div className={styles.dialogContent}>
            <div className={styles.dialogMap}>
              <GoogleMap
                options={dialogMapOptions}
                onLoad={(map) => onMapLoad(map, 50)}
                mapContainerClassName={styles.mapContainer}
              >
                {markers.map((m) => (
                  <Marker
                    position={m}
                    key={m.fillColor}
                    icon={getMarkerIcon(m.fillColor, 3)}
                  />
                ))}
              </GoogleMap>
            </div>
            <div className={styles.dialogDescription}>
              {!!targetWarehouse?.participant && (
                <ParticipantInfo
                  isStaffView={isStaffView}
                  participant={targetWarehouse.participant}
                  isCompanyActive={targetWarehouse.isCompanyActive}
                />
              )}
              {markers.map((marker) => (
                <div
                  key={marker.fillColor}
                  className={styles.markerDescription}
                >
                  <MarkerLocation fillColor={marker.fillColor} />
                  <div>
                    <div className={styles.title}>{marker.title}</div>
                    <div className={styles.subtitle}>{marker.subtitle}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default LocationsMap;
